import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";

interface DealerCurrency {
  id: string;
  dealer_code: string;
  dealer_name: string;
  is_active: boolean;
  currencies: string[];
}

const AVAILABLE_CURRENCIES = [
  { code: "TRY", label: "TL", symbol: "$", flag: "🇹🇷" },
  { code: "USD", label: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "EUR", label: "EUR", symbol: "€", flag: "🇪🇺" },
  { code: "GBP", label: "GBP", symbol: "£", flag: "🇬🇧" },
];

export default function AdminDealerCurrenciesPage() {
  const [dealers, setDealers] = useState<DealerCurrency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingDealer, setEditingDealer] = useState<DealerCurrency | null>(null);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("dealers")
        .select("id, dealer_code, dealer_name, is_active, currencies")
        .order("created_at", { ascending: false });

      if (err) throw err;
      setDealers((data || []) as DealerCurrency[]);
    } catch (e: any) {
      setError(e.message || "Bayiler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  const openEdit = (d: DealerCurrency) => {
    setEditingDealer(d);
    setSelectedCurrencies([...(d.currencies || ["TRY"])]);
    setSuccessMsg("");
  };

  const toggleCurrency = (code: string) => {
    setSelectedCurrencies((prev) => {
      if (prev.includes(code)) {
        if (prev.length === 1) return prev;
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });
  };

  const saveCurrencies = async () => {
    if (!editingDealer || selectedCurrencies.length === 0) return;
    setSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase
        .from("dealers")
        .update({ currencies: selectedCurrencies })
        .eq("id", editingDealer.id);

      if (err) throw err;

      setSuccessMsg(`${editingDealer.dealer_name} için para birimleri güncellendi.`);
      setDealers((prev) =>
        prev.map((d) =>
          d.id === editingDealer.id ? { ...d, currencies: selectedCurrencies } : d
        )
      );
      setEditingDealer(null);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e: any) {
      setError(e.message || "Güncellenirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const getCurrencyLabel = (code: string) => {
    const found = AVAILABLE_CURRENCIES.find((c) => c.code === code);
    return found ? `${found.flag} ${found.label} (${found.symbol})` : code;
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="dealer-currencies">Bayi Para Birimleri</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Her bayi için geçerli para birimlerini belirleyin. Birden fazla seçilebilir.</p>
        </div>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-check-line"></i>
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          {/* Desktop Table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm min-w-[650px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Kodu</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Adı</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Para Birimleri</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {dealers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-foreground-400 text-sm">
                      <i className="ri-store-2-line text-3xl block mb-2"></i>
                      Henüz bayi eklenmemiş.
                    </td>
                  </tr>
                ) : (
                  dealers.map((d) => (
                    <tr key={d.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900">{d.dealer_code}</td>
                      <td className="px-4 py-3 text-foreground-900">{d.dealer_name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${d.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                          {d.is_active ? "Aktif" : "Pasif"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {(d.currencies || ["TRY"]).map((code) => (
                            <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700">
                              {getCurrencyLabel(code)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => openEdit(d)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-600 hover:bg-accent-100 cursor-pointer mx-auto" title="Para Birimi Düzenle">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-background-200/70">
            {dealers.length === 0 ? (
              <div className="px-4 py-10 text-center text-foreground-400 text-sm">
                <i className="ri-store-2-line text-3xl block mb-2"></i>
                Henüz bayi eklenmemiş.
              </div>
            ) : (
              dealers.map((d) => (
                <div key={d.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-foreground-900 text-sm">{d.dealer_name}</p>
                      <p className="text-xs text-foreground-400 font-mono">{d.dealer_code}</p>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${d.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                      {d.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(d.currencies || ["TRY"]).map((code) => (
                      <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700">
                        {getCurrencyLabel(code)}
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <button onClick={() => openEdit(d)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-600 hover:bg-accent-100 cursor-pointer active:scale-[0.98]"><i className="ri-edit-line text-sm"></i></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Edit Currency Modal */}
      {editingDealer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setEditingDealer(null)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-base font-semibold text-foreground-950"><a id="currency-form">Para Birimi Düzenle</a></h4>
                <p className="text-sm text-foreground-500 mt-0.5">{editingDealer.dealer_name}</p>
              </div>
              <button onClick={() => setEditingDealer(null)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>

            <div className="bg-background-100 rounded-xl p-4 mb-1">
              <p className="text-xs font-medium text-foreground-500 mb-3">Geçerli Para Birimleri (en az bir tane seçilmeli)</p>
              <div className="space-y-2">
                {AVAILABLE_CURRENCIES.map((curr) => {
                  const isSelected = selectedCurrencies.includes(curr.code);
                  return (
                    <button
                      key={curr.code}
                      onClick={() => toggleCurrency(curr.code)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${isSelected ? "bg-accent-100 border border-accent-200 text-accent-700" : "bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-200/70"}`}
                    >
                      <span className="text-lg">{curr.flag}</span>
                      <span className="flex-1 text-left">{curr.label}</span>
                      <span className="text-foreground-400 font-mono">{curr.symbol}</span>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isSelected ? "border-accent-500 bg-accent-500" : "border-background-300/60"}`}>
                        {isSelected && <i className="ri-check-line text-background-50 text-xs"></i>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditingDealer(null)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={saveCurrencies} disabled={saving || selectedCurrencies.length === 0} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}